import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Plus, MoreHorizontal, Archive, FileText, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { usePricingPolicies, usePricingMutations } from '@/hooks/usePricing'
import { POLICY_STATUS_LABELS, type PricingPolicy } from '@/types/pricing'
import { PolicyCreateDialog } from './PolicyCreateDialog'

export function PoliciesTab() {
  const { data: policies, isLoading } = usePricingPolicies()
  const mutations = usePricingMutations()
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  const activePolicies = policies?.filter((p) => p.status !== 'archived') ?? []
  const archivedPolicies = policies?.filter((p) => p.status === 'archived') ?? []

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Pricing Policies</CardTitle>
              <CardDescription>
                Rate cards that can be assigned to locations. Each policy contains versioned rate sheets.
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Policy
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {activePolicies.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No pricing policies yet</p>
              <p className="mt-1 text-sm">Create your first policy to define rate cards.</p>
              <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Policy
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {activePolicies.map((policy) => (
                  <PolicyRow
                    key={policy.id}
                    policy={policy}
                    onArchive={() => mutations.archivePolicy.mutate(policy.id)}
                    onClick={() => {}}
                  />
                ))}
              </TableBody>
            </Table>
          )}

          {archivedPolicies.length > 0 && (
            <div className="mt-8">
              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                Archived ({archivedPolicies.length})
              </h4>
              <Table>
                <TableBody>
                  {archivedPolicies.map((policy) => (
                    <PolicyRow key={policy.id} policy={policy} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <PolicyCreateDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </>
  )
}

function PolicyRow({
  policy,
  onArchive,
}: {
  policy: PricingPolicy
  onArchive?: () => void
  onClick?: () => void
}) {
  const navigate = useNavigate()
  const statusVariant =
    policy.status === 'active'
      ? 'default'
      : policy.status === 'draft'
        ? 'secondary'
        : 'outline'

  return (
    <TableRow
      className="cursor-pointer"
      onClick={() => navigate(`/settings/pricing/policies/${policy.id}`)}
    >
      <TableCell className="font-medium">{policy.name}</TableCell>
      <TableCell>
        <Badge variant="outline" className="capitalize">
          {policy.scope}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant={statusVariant}>
          {POLICY_STATUS_LABELS[policy.status]}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground max-w-[300px] truncate">
        {policy.description || '-'}
      </TableCell>
      <TableCell>
        {onArchive && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation()
                navigate(`/settings/pricing/policies/${policy.id}`)
              }}>
                <ExternalLink className="mr-2 h-4 w-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation()
                onArchive()
              }}>
                <Archive className="mr-2 h-4 w-4" />
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  )
}
